/**
 * Analytics Module — Phase 17.2.1
 *
 * Public API for event tracking. Integrates the pure-logic tracker
 * with Supabase persistence.
 *
 * Usage:
 *   import { trackEvent, initAnalytics, stopAnalytics } from '@/core/analytics';
 *   initAnalytics(userId);
 *   trackEvent('novel_created', { genre: 'fantasy' });
 */

import { supabase } from '@/core/supabase';
import { createLogger } from '@/shared/utils/logger';
import {
  createEventQueue,
  sanitizeProperties,
  isValidEventName,
  generateSessionId,
  type AnalyticsEventName,
  type EventPropertyMap,
  type QueuedEvent,
} from './tracker';
import { captureEvent as phCapture } from './posthog';

const analyticsLog = createLogger('Analytics');

let sessionId: string | null = null;
let userId: string | null = null;
let queue: ReturnType<typeof createEventQueue> | null = null;

/**
 * Persist a batch of events to Supabase.
 * Errors are caught and logged — analytics should never break the app.
 */
async function persistBatch(events: QueuedEvent[]): Promise<void> {
  // Capture module-level state at call time — stop() may null them
  // before the async insert completes
  const uid = userId;
  const sid = sessionId;
  if (!uid || events.length === 0) return;
  try {
    const rows = events.map((e) => ({
      user_id: uid,
      event_name: e.event_name,
      properties: e.properties,
      session_id: sid,
    }));
    const { error } = await supabase.from('analytics_events').insert(rows);
    if (error) {
      analyticsLog.debug('persist error', { error: error.message });
    }
  } catch (err) {
    analyticsLog.debug('persist exception', { error: String(err) });
  }
}

/**
 * Initialize analytics for the current user session.
 * Call after authentication.
 */
export function initAnalytics(uid: string): void {
  userId = uid;
  sessionId = generateSessionId();
  if (queue) queue.stop();
  queue = createEventQueue(persistBatch);
  queue.start();
}

/**
 * Stop analytics and flush remaining events.
 * Call on logout or app unmount.
 */
export function stopAnalytics(): void {
  if (queue) {
    queue.stop();
    queue = null;
  }
  userId = null;
  sessionId = null;
}

/**
 * Track a single event. Type-safe properties via EventPropertyMap.
 *
 * Events are queued and batched for efficiency.
 * Invalid event names or missing initialization are silently ignored.
 */
export function trackEvent<E extends AnalyticsEventName>(
  eventName: E,
  properties?: EventPropertyMap[E],
): void {
  if (!queue || !userId) return;
  if (!isValidEventName(eventName)) return;

  const sanitized = sanitizeProperties((properties ?? {}) as Record<string, unknown>);
  queue.enqueue({
    event_name: eventName,
    properties: sanitized,
    timestamp: Date.now(),
  });

  // Dual-send to PostHog (fire-and-forget, never blocks)
  phCapture(eventName, sanitized);
}

// Re-export types for consumers
export type { AnalyticsEventName, EventPropertyMap } from './tracker';